import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Home as HomeIcon, 
  User as UserIcon, 
  Briefcase as ProjectsIcon, 
  Sparkles as SkillsIcon, 
  Mail as MailIcon,
  Moon, 
  Sun, 
  Github, 
  Linkedin 
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { MobileNav } from './MobileNav';
import { ExpandedTabs, TabItem, Tab } from './ExpandedTabs';
import { scrollToSection, isProgrammaticScrollActive } from '../utils/scroll';
import { MagneticButton } from './MagneticButton';

export const Navbar: React.FC = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const [activeSection, setActiveSection] = useState<string>('hero');
  const rafRef = useRef<number | null>(null);

  const navTabs: TabItem[] = [
    { title: 'Home', icon: HomeIcon, id: 'hero' },
    { title: 'About', icon: UserIcon, id: 'about' },
    { title: 'Projects', icon: ProjectsIcon, id: 'projects' },
    { title: 'Skills', icon: SkillsIcon, id: 'skills' },
    { type: 'separator' },
    { title: 'Contact', icon: MailIcon, id: 'contact' },
  ];

  const handleNavClick = (sectionId: string) => {
    setActiveSection(sectionId);
    scrollToSection(sectionId);
  };

  const handleTabClick = (tab: Tab) => {
    if (tab.id) {
      handleNavClick(tab.id);
    }
  };

  // Scroll spy with RAF throttling and programmatic scroll lock protection
  useEffect(() => {
    const handleScroll = () => {
      if (isProgrammaticScrollActive()) return;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      rafRef.current = requestAnimationFrame(() => {
        const sections = ['hero', 'about', 'projects', 'skills', 'contact'];
        const scrollPosition = window.scrollY + 180;

        if (window.scrollY < 200) {
          setActiveSection('hero');
          return;
        }

        for (let i = sections.length - 1; i >= 0; i--) {
          const sectionId = sections[i];
          const element = document.getElementById(sectionId);
          if (element) {
            const top = element.offsetTop;
            if (scrollPosition >= top) {
              setActiveSection(sectionId);
              break;
            }
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="fixed top-0 inset-x-0 w-full z-50 backdrop-blur-xl bg-white/75 dark:bg-brand-dark-bg/80 border-b border-brand-light/30 dark:border-brand-purple/30 transition-colors duration-300 max-w-[100vw]"
    >
      <div className="w-full max-w-7xl mx-auto px-3.5 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20 w-full">
          {/* Logo */}
          <div className="shrink-0">
            <MagneticButton>
              <button
                type="button"
                onClick={() => handleNavClick('hero')}
                className="flex items-center gap-2.5 text-xl xs:text-2xl font-extrabold bg-gradient-to-r from-brand-navy via-brand-purple to-brand-blue dark:from-white dark:via-brand-light dark:to-brand-purple bg-clip-text text-transparent font-inter cursor-pointer tracking-tight group"
                data-cursor="button"
              >
                <img
                  src="/assets/logo.png"
                  alt="Nugi Logo"
                  className="w-7 h-7 sm:w-8 sm:h-8 object-contain rounded-lg transition-transform duration-300 group-hover:scale-105 select-none"
                />
                <span>nugi<span className="text-brand-purple dark:text-brand-light">.dev</span></span>
              </button>
            </MagneticButton>
          </div>

          {/* Desktop Animated Expanded Tabs Navigation (1024px+) */}
          <div className="hidden lg:flex items-center justify-center">
            <ExpandedTabs
              tabs={navTabs}
              activeId={activeSection}
              onTabClick={handleTabClick}
            />
          </div>

          {/* Action Icons, Theme Toggle & Mobile Menu Drawer Trigger */}
          <div className="flex items-center gap-1.5 xs:gap-2 sm:gap-3 shrink-0">
            {/* Desktop Social Icons (1280px+) */}
            <div className="hidden xl:flex items-center gap-1.5">
              <MagneticButton>
                <a
                  href="https://github.com/Nugi29"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                  className="p-2.5 rounded-full text-brand-navy dark:text-brand-dark-text hover:text-brand-purple dark:hover:text-brand-light hover:bg-brand-purple/10 dark:hover:bg-brand-purple/20 transition-all duration-200 block cursor-pointer"
                  data-cursor="button"
                >
                  <Github size={18} />
                </a>
              </MagneticButton>

              <MagneticButton>
                <a
                  href="https://www.linkedin.com/in/nugitha-disas"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="p-2.5 rounded-full text-brand-navy dark:text-brand-dark-text hover:text-brand-purple dark:hover:text-brand-light hover:bg-brand-purple/10 dark:hover:bg-brand-purple/20 transition-all duration-200 block cursor-pointer"
                  data-cursor="button"
                >
                  <Linkedin size={18} />
                </a>
              </MagneticButton>

              <MagneticButton>
                <a
                  href="mailto:nugithadc@gmail.com"
                  aria-label="Email"
                  className="p-2.5 rounded-full text-brand-navy dark:text-brand-dark-text hover:text-brand-purple dark:hover:text-brand-light hover:bg-brand-purple/10 dark:hover:bg-brand-purple/20 transition-all duration-200 block cursor-pointer"
                  data-cursor="button"
                >
                  <MailIcon size={18} />
                </a>
              </MagneticButton>
            </div>

            {/* Theme Toggle Button */}
            <MagneticButton>
              <button
                type="button"
                onClick={toggleDarkMode}
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                className="p-2 xs:p-2.5 rounded-2xl bg-brand-purple/10 dark:bg-brand-purple/25 text-brand-navy dark:text-brand-dark-text border border-brand-purple/30 dark:border-brand-purple/40 hover:border-brand-purple/60 hover:bg-brand-purple/20 dark:hover:bg-brand-purple/40 transition-all duration-300 cursor-pointer"
                data-cursor="button"
              >
                {darkMode ? (
                  <Sun size={18} className="text-amber-300" />
                ) : (
                  <Moon size={18} className="text-brand-purple" />
                )}
              </button>
            </MagneticButton>

            {/* Mobile & Tablet Navigation Drawer Trigger (<1024px) */}
            <div className="lg:hidden shrink-0">
              <MobileNav scrollToSection={handleNavClick} activeSection={activeSection} />
            </div>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Navbar;